import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from '@formspree/react';
import { useTranslation } from '../hooks/useTranslation';
import ReferralProgram from '../components/ReferralProgram';
import './Contact.css';

const Contact = () => {
  const { t } = useTranslation();
  const refs = useRef([]);
  const [state, handleSubmit] = useForm("maqdoaob");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  // Field refs for focus on error
  const fieldRefs = {
    name: useRef(null),
    phone: useRef(null),
    email: useRef(null),
    propertyType: useRef(null),
    serviceRequired: useRef(null),
    location: useRef(null),
    message: useRef(null),
  };

  // Validation functions
  const validatePhone = (phone) => {
    const cleaned = phone.replace(/[\s\-+]/g, '');
    return /^\d{10,12}$/.test(cleaned);
  };

  const validateEmail = (email) => {
    if (!email) return true; // Email is optional
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateForm = (formData) => {
    const newErrors = {};

    if (!formData.get('name')?.trim()) {
      newErrors.name = t('contact.form.errors.nameRequired') || 'Full name is required';
    }

    const phone = formData.get('phone');
    if (!phone?.trim()) {
      newErrors.phone = t('contact.form.errors.phoneRequired') || 'Phone number is required';
    } else if (!validatePhone(phone)) {
      newErrors.phone = t('contact.form.errors.phoneInvalid') || 'Enter a valid phone number (10-12 digits)';
    }

    const email = formData.get('email');
    if (email && !validateEmail(email)) {
      newErrors.email = t('contact.form.errors.emailInvalid') || 'Enter a valid email address';
    }

    if (!formData.get('propertyType')) {
      newErrors.propertyType = t('contact.form.errors.propertyRequired') || 'Please select property type';
    }

    if (!formData.get('serviceRequired')) {
      newErrors.serviceRequired = t('contact.form.errors.serviceRequired') || 'Please select service type';
    }

    return newErrors;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setIsSubmitting(true);

    // Check terms acceptance first
    if (!acceptTerms) {
      setErrors({ acceptTerms: t('terms.checkbox.error') || 'Please accept the Terms & Conditions to proceed' });
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData(e.target);
    const validationErrors = validateForm(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      // Focus on first error field
      const firstErrorField = Object.keys(validationErrors)[0];
      fieldRefs[firstErrorField]?.current?.focus();
      return;
    }

    try {
      await handleSubmit(e);
    } catch (err) {
      console.error('Form submission error:', err);
    }
    setIsSubmitting(false);
  };

  // Handle API errors from Formspree
  useEffect(() => {
    if (state.errors && state.errors.length > 0) {
      const apiErrors = {};
      state.errors.forEach(error => {
        const fieldName = error.field?.toLowerCase();
        if (fieldName === 'phone') {
          apiErrors.phone = t('contact.form.errors.phoneInvalid') || 'Enter a valid phone number';
        } else if (fieldName === 'email') {
          apiErrors.email = t('contact.form.errors.emailInvalid') || 'Enter a valid email address';
        } else if (fieldName === 'name') {
          apiErrors.name = t('contact.form.errors.nameRequired') || 'Full name is required';
        } else {
          apiErrors.general = error.message || 'An error occurred. Please try again.';
        }
      });
      setErrors(apiErrors);
      // Focus on first error field
      const firstErrorField = Object.keys(apiErrors)[0];
      if (firstErrorField !== 'general' && fieldRefs[firstErrorField]) {
        fieldRefs[firstErrorField]?.current?.focus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.errors, t]);

  const contactInfo = [
    {
      icon: '📞',
      label: t('contact.info.phone'),
      value: '+91 94217 22575',
      link: 'tel:+919421722575',
    },
    {
      icon: '✉️',
      label: t('contact.info.email'),
      value: 'shivshrienterprises2628@gmail.com',
      link: 'mailto:shivshrienterprises2628@gmail.com',
    },
    {
      icon: '📍',
      label: t('contact.info.headOffice'),
      value: t('contact.info.headOfficeValue'),
      link: null,
    },
    {
      icon: '🕐',
      label: t('contact.info.workingHours'),
      value: t('contact.info.workingHoursValue'),
      link: null,
    },
    {
      icon: '🗺️',
      label: t('contact.info.serviceArea'),
      value: t('contact.info.serviceAreaValue'),
      link: null,
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.08 }
    );
    refs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);
  const add = el => { if (el && !refs.current.includes(el)) refs.current.push(el); };

  return (
    <div>
      <section className="page-hero">
        <div className="container page-hero__inner" style={{ gridTemplateColumns: '1fr' }}>
          <div className="reveal text-center" ref={add}>
            <div className="section-badge" style={{ justifyContent: 'center', display: 'inline-flex' }}>{t('contact.hero.badge')}</div>
            <div className="vocal-local-badge" style={{ justifyContent: 'center', margin: '0.75rem auto 0' }}>
              <span>🇮🇳</span>
              <span className="vocal-text">{t('home.hero.vocalForLocal') || 'Vocal for Local'}</span>
              <span className="vocal-divider">|</span>
              <span className="vocal-sub">{t('home.hero.kolhapursOwn') || "Kolhapur's Own"}</span>
            </div>
            <h1>{t('contact.hero.title1')} <em style={{ fontStyle: 'italic', color: 'var(--green)' }}>{t('common.companyNameFull')}</em></h1>
            <p style={{ color: 'var(--text-mid)', fontSize: '1.05rem', lineHeight: '1.75', maxWidth: '540px', margin: '1rem auto 0' }}>
              {t('contact.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding contact-section">
        <div className="container contact-layout">
          {/* LEFT INFO */}
          <div className="contact-info reveal-left" ref={add}>
            <h2 style={{ fontFamily: 'Fraunces', color: 'var(--navy)', marginBottom: '0.5rem', fontSize: 'clamp(1.6rem,2.5vw,2rem)' }}>{t('contact.info.letsTalk')}</h2>
            <p style={{ color: 'var(--text-mid)', marginBottom: '2rem', fontSize: '0.95rem', lineHeight: '1.75' }}>
              {t('contact.info.subtitle')}
            </p>

            <div className="contact-info-list">
              {contactInfo.map(item => (
                <div key={item.label} className="contact-info-item">
                  <div className="contact-info-icon">{item.icon}</div>
                  <div>
                    <div className="contact-info-label">{item.label}</div>
                    {item.link ? (
                      <a href={item.link} className="contact-info-value contact-info-link">{item.value}</a>
                    ) : (
                      <div className="contact-info-value" style={{ whiteSpace: 'pre-line' }}>{item.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="contact-gem-promo">
              <div className="contact-gem-promo-icon">🏛️</div>
              <div>
                <strong>{t('contact.hero.badge')}</strong>
                <p>{t('contact.info.subtitle')}</p>
              </div>
            </div>
          </div>

          {/* RIGHT FORM */}
          <div className="contact-form-wrap reveal-right" ref={add}>
            <div className="contact-form">
              <h3 className="contact-form-title">{t('contact.form.title')}</h3>
              <p className="contact-form-sub">{t('contact.form.subtitle')}</p>

              {state.succeeded ? (
                <div className="contact-success">
                  <div className="contact-success-icon">✓</div>
                  <h4>{t('contact.form.successTitle')}</h4>
                  <p>{t('contact.form.successMsg')}</p>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit} noValidate>
                  {errors.general && (
                    <div className="contact-form-error contact-form-error--general">
                      <span className="contact-form-error-icon">⚠️</span>
                      {errors.general}
                    </div>
                  )}
                  <div className="form-row">
                    <div className={`contact-form-group ${errors.name ? 'has-error' : ''}`}>
                      <label>{t('contact.form.fullName')} *</label>
                      <input 
                        type="text" 
                        name="name" 
                        ref={fieldRefs.name}
                        placeholder={t('contact.form.fullName')} 
                        className={errors.name ? 'input-error' : ''}
                        onChange={() => errors.name && setErrors(prev => ({ ...prev, name: null }))}
                      />
                      {errors.name && <span className="contact-form-error">{errors.name}</span>}
                    </div>
                    <div className={`contact-form-group ${errors.phone ? 'has-error' : ''}`}>
                      <label>{t('contact.form.phone')} *</label>
                      <input 
                        type="tel" 
                        name="phone" 
                        ref={fieldRefs.phone}
                        placeholder="+91 00000 00000" 
                        className={errors.phone ? 'input-error' : ''}
                        onChange={() => errors.phone && setErrors(prev => ({ ...prev, phone: null }))}
                      />
                      {errors.phone && <span className="contact-form-error">{errors.phone}</span>}
                    </div>
                  </div>
                  <div className={`contact-form-group ${errors.email ? 'has-error' : ''}`}>
                    <label>{t('contact.form.email')}</label>
                    <input 
                      type="email" 
                      name="email" 
                      ref={fieldRefs.email}
                      placeholder={t('contact.form.emailPlaceholder')} 
                      className={errors.email ? 'input-error' : ''}
                      onChange={() => errors.email && setErrors(prev => ({ ...prev, email: null }))}
                    />
                    {errors.email && <span className="contact-form-error">{errors.email}</span>}
                  </div>
                  <div className="form-row">
                    <div className={`contact-form-group ${errors.propertyType ? 'has-error' : ''}`}>
                      <label>{t('contact.form.propertyType')} *</label>
                      <select 
                        name="propertyType" 
                        ref={fieldRefs.propertyType}
                        className={errors.propertyType ? 'input-error' : ''}
                        onChange={() => errors.propertyType && setErrors(prev => ({ ...prev, propertyType: null }))}
                      >
                        <option value="">{t('contact.form.propertyType')}...</option>
                        {t('contact.form.propertyTypeOptions').map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                      {errors.propertyType && <span className="contact-form-error">{errors.propertyType}</span>}
                    </div>
                    <div className={`contact-form-group ${errors.serviceRequired ? 'has-error' : ''}`}>
                      <label>{t('contact.form.serviceRequired')} *</label>
                      <select 
                        name="serviceRequired" 
                        ref={fieldRefs.serviceRequired}
                        className={errors.serviceRequired ? 'input-error' : ''}
                        onChange={() => errors.serviceRequired && setErrors(prev => ({ ...prev, serviceRequired: null }))}
                      >
                        <option value="">{t('contact.form.serviceRequired')}...</option>
                        {t('contact.form.serviceRequiredOptions').map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                      {errors.serviceRequired && <span className="contact-form-error">{errors.serviceRequired}</span>}
                    </div>
                  </div>
                  <div className="contact-form-group">
                    <label>{t('contact.form.location')}</label>
                    <input 
                      type="text" 
                      name="location" 
                      ref={fieldRefs.location}
                      placeholder={t('contact.form.locationPlaceholder')} 
                    />
                  </div>
                  <div className="contact-form-group">
                    <label>{t('contact.form.additionalDetails')}</label>
                    <textarea 
                      name="message" 
                      ref={fieldRefs.message}
                      rows="4" 
                      placeholder={t('contact.form.additionalDetailsPlaceholder')}
                    ></textarea>
                  </div>
                  
                  {/* Terms Checkbox */}
                  <div className="terms-checkbox-container">
                    <label className={`terms-checkbox-label ${errors.acceptTerms ? 'has-error' : ''}`}>
                      <input
                        type="checkbox"
                        checked={acceptTerms}
                        onChange={(e) => {
                          setAcceptTerms(e.target.checked);
                          if (errors.acceptTerms) setErrors(prev => ({ ...prev, acceptTerms: null }));
                        }}
                      />
                      <span className="checkmark"></span>
                      <span className="terms-text">
                        {t('terms.checkbox.text') || 'I have read and agree to the'} <Link to="/terms" target="_blank">{t('terms.checkbox.link') || 'Terms & Conditions'}</Link>
                      </span>
                    </label>
                    {errors.acceptTerms && <span className="error-message">{errors.acceptTerms}</span>}
                  </div>

                  <button type="submit" className="btn btn-green contact-submit" disabled={isSubmitting || state.submitting}>
                    {(isSubmitting || state.submitting) ? (
                      <><span className="contact-submit-spinner"></span> {t('contact.form.submitting') || 'Sending...'}</>
                    ) : (
                      t('contact.form.submit')
                    )}
                  </button>
                  <p className="contact-form-note">{t('contact.form.note')}</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* REFERRAL PROGRAM */}
      <ReferralProgram />

      {/* MAP PLACEHOLDER */}
      <section style={{ background: 'var(--blue-pale)', borderTop: '1px solid var(--border)', padding: '4rem 0' }}>
        <div className="container">
          <div className="text-center reveal" ref={add} style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontFamily: 'Fraunces', color: 'var(--navy)', fontSize: '1.5rem' }}>{t('footer.gargoti')} - {t('footer.kolhapurCity')}</h3>
            <p style={{ color: 'var(--text-muted)', marginTop: '0.4rem' }}>{t('footer.allKolhapur')}</p>
          </div>
          <div className="map-embed reveal" ref={add}>
            <iframe
              title="ShivShri Enterprises Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d30671.12!2d74.00!3d16.35!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc00!2sGargoti!5e0!3m2!1sen!2sin!4v1"
              width="100%"
              height="360"
              style={{ border: 0, borderRadius: '16px', display: 'block' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <div className="service-areas reveal" ref={add}>
            <h4>{t('contact.areas.title')}</h4>
            <div className="service-areas-grid">
              {t('contact.areas.list').map(a => (
                <div key={a} className="service-area-tag">📍 {a}</div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
